import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  APIResponse,
  PaginatedResponse,
  LoginRequest,
  LoginResponse,
  User,
  Product,
  Category,
  Tag,
  Order,
  OnlineOrder,
  ActiveOnlineOrder,
  StationKOT,
  OfflineReplayRequest,
  UnreconciledOfflineItem,
  Payment,
  CreateOrderRequest,
  UpdateOrderStatusRequest,
  ProcessPaymentRequest,
  PaymentSummary,
  DashboardStats,
  OrderFilters,
  UpdateCounterOrderGuestRequest,
  ProductFilters,
  StockItem,
  StockMovement,
  InventoryActivityEntry,
  StockAlert,
  UserBrief,
  StockSummary,
  AdvancedStockReport,
  Supplier,
  PurchaseOrderSummary,
  PurchaseOrderDetail,
  VendorBill,
  VendorAgingRow,
  VendorLedgerEntry,
  Expense,
  DailyClosing,
  PnLReport,
  CurrentDayStatus,
  AgingOpenOrdersResult,
  TenderReconResult,
  ExpenseSummary,
  ExpenseIntelligenceReport,
  ExpenseCategoryDefinition,
  VoidLogEntry,
  PricingSettings,
  OnlineSubMethodConfig,
  OverviewReport,
  DailySalesRow,
  HourlySalesReport,
  ItemSalesRow,
  OrdersBrowserResponse,
  InvoiceSearchRow,
  RefundDailyRow,
  RefundLogRow,
  ReportsExportId,
  TaxSummaryReport,
  StaffPerformanceReport,
  DashboardOverview,
  DashboardPeriod,
  LivePulse,
  SalesTimeseries,
  DashboardTopItem,
  PaymentMixSlice,
  OrderTypeMixSlice,
  DashboardAlert,
  FiscalConfigPublic,
  FiscalTestConnectionResult,
  FiscalDiSn002SandboxValidateResult,
  BusinessDay,
  CashDrawerMovement,
  ShiftClosing,
  DayCloseAuditEntry,
  FiscalNote,
  CreateFiscalNoteRequest,
  IssueCreditNoteRequest,
  FiscalAuditEvent,
  FiscalJob,
  PRAUnresolvedClaim,
  ZReportPayload,
  MarketingQRListResponse,
  MarketingQRTimeseriesResponse,
  UpdateMarketingQRRequest,
  MarketingOverviewResponse,
  VendorSalesReport,
  VendorStockReport,
  VendorSettlement,
} from '@/types';
import { markBackendReachable, noteRequestFailure } from '@/lib/offline/reachability';

/**
 * Error thrown by every {@link APIClient.request} on non-2xx responses.
 * Carries the parsed backend payload so callers can branch on machine-
 * readable codes (e.g. `previous_day_open`) without scraping the
 * human-readable message.
 */
export class ApiClientError extends Error {
  /** Machine-readable error code from the backend payload (`error` field). */
  code?: string;
  /** HTTP status code from the response. */
  status?: number;
  /** Optional list of orders blocking the operation (table reassign, etc.). */
  blockingOrders?: Array<{ id?: string; order_number?: string; status?: string }>;
  /**
   * Tender keys the backend refused to close without, on `tender_count_required`
   * (F-CTRL-N33) — e.g. `['card']`. Lets the close wizard reopen on that exact
   * step instead of stranding the operator on a Finish button that keeps
   * failing. Read from `data.data`, since the envelope invariant puts payloads
   * under `Data`.
   */
  missingTenders?: string[];
  /**
   * True when no HTTP response was received — i.e. a transport failure (no
   * connection) OR a timeout after the request may have reached the server.
   * Callers use this to keep a staged order instead of discarding it.
   */
  isNetworkError?: boolean;
  /**
   * Raw axios error code when available. `ERR_NETWORK` = request never left the
   * device (safe to re-place); `ECONNABORTED` = timeout, the server may have
   * received it (ambiguous — warn before re-placing to avoid a duplicate).
   */
  axiosCode?: string;
  constructor(message: string) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/** Axios requests use `/auth/...`, `/admin/...` — base must include `/api/v1` from Gin's router.Group. */
function resolveApiBaseUrl(): string {
  const raw =
    typeof import.meta.env?.VITE_API_URL === 'string' && import.meta.env.VITE_API_URL.trim() !== ''
      ? import.meta.env.VITE_API_URL.trim()
      : 'http://localhost:8080/api/v1'
  const base = raw.replace(/\/+$/, '')
  if (/\/api\/v1$/i.test(base)) return base
  return `${base}/api/v1`
}

class APIClient {
  private client: AxiosInstance;

  constructor() {
    const apiUrl = resolveApiBaseUrl();
    console.log('🔧 API Client baseURL:', apiUrl);
    console.log('🔧 Environment VITE_API_URL:', import.meta.env?.VITE_API_URL);
    
    this.client = axios.create({
      baseURL: apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('pos_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          config.headers['X-POS-JWT'] = token;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const payload = error.response?.data as { error?: string } | undefined;
          const errCode = typeof payload?.error === 'string' ? payload.error : '';
          // Proxies may strip Authorization — not an expired session.
          if (errCode !== 'missing_auth_header') {
            localStorage.removeItem('pos_token');
            localStorage.removeItem('pos_user');
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Helper method to handle API responses
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.request(config);
      // F-FE-N30: a real response proves the backend is up. This is the single
      // HTTP choke point (invariant #9), so it is the only place that needs to
      // know — every caller inherits an accurate signal for free.
      markBackendReachable();
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as
          | {
              message?: string;
              error?: string;
              blocking_orders?: Array<{ id?: string; order_number?: string; status?: string }>;
              data?: { missing_tenders?: string[] };
            }
          | undefined;
        const parts = [data?.message, data?.error].filter(Boolean);
        let msg = parts.length > 0 ? parts.join(' — ') : error.message;
        if (data?.blocking_orders?.length) {
          const detail = data.blocking_orders
            .map((o) => `${o.order_number ?? o.id ?? '?'} (${o.status ?? 'unknown'})`)
            .join(', ');
          msg = `${msg}. Blocking: ${detail}`;
        }
        // Surface structured fields on the Error so callers (counter UI,
        // day-close page) can branch on machine-readable codes like
        // `previous_day_open` without parsing the message string.
        const err = new ApiClientError(msg);
        err.code = data?.error;
        err.status = error.response?.status;
        err.blockingOrders = data?.blocking_orders;
        err.missingTenders = data?.data?.missing_tenders;
        // No response => transport failure (offline) or a post-send timeout.
        // Preserve the axios code so callers can tell "never sent" (ERR_NETWORK)
        // from "sent, outcome unknown" (ECONNABORTED) — the duplicate-order trap.
        err.isNetworkError = !error.response;
        err.axiosCode = error.code;
        // F-FE-N30: decide whether this failure means the BACKEND is gone, as
        // opposed to a server bug or a business-rule rejection. Narrow on
        // purpose — see noteRequestFailure.
        noteRequestFailure({
          isNetworkError: err.isNetworkError,
          status: err.status,
          code: err.code,
        });
        throw err;
      }
      throw error;
    }
  }

  /**
   * Backend exposes POST /counter/orders and /admin/orders — not POST /orders.
   * Order creation must use the route group that matches the logged-in role.
   */
  private getStoredUserRole(): string {
    try {
      const raw = localStorage.getItem('pos_user');
      if (raw) {
        const u = JSON.parse(raw) as { role?: string };
        if (u.role) return u.role;
      }
    } catch {
      /* ignore */
    }
    return 'counter';
  }

  private getOrderCreatePath(): string {
    const role = this.getStoredUserRole();
    if (role === 'admin') return '/admin/orders';
    if (role === 'counter' || role === 'manager') return '/counter/orders';
    return '/counter/orders';
  }

  private getProcessPaymentPath(orderId: string): string {
    const role = this.getStoredUserRole();
    if (role === 'admin') return `/admin/orders/${orderId}/payments`;
    if (role === 'counter' || role === 'manager') return `/counter/orders/${orderId}/payments`;
    return `/counter/orders/${orderId}/payments`;
  }

  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<APIResponse<LoginResponse>> {
    return this.request({
      method: 'POST',
      url: '/auth/login',
      data: credentials,
    });
  }

  async logout(): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/auth/logout',
    });
  }

  async getCurrentUser(): Promise<APIResponse<User>> {
    return this.request({
      method: 'GET',
      url: '/auth/me',
    });
  }

  /**
   * Starts a password-reset flow. The backend always responds 200 with a
   * generic message — we never signal whether the email exists. Rate
   * limiting lives on the server.
   */
  async forgotPassword(email: string): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/auth/forgot-password',
      data: { email },
    });
  }

  /**
   * Completes a password reset using the one-time token embedded in the
   * email link. `newPassword` must be at least 8 characters; the backend
   * enforces the same rule.
   */
  async resetPassword(token: string, newPassword: string): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/auth/reset-password',
      data: { token, new_password: newPassword },
    });
  }

  /**
   * Rotates the currently-signed-in user's password. Requires current
   * password as anti-session-hijack guard. Does NOT invalidate existing
   * JWTs — the caller keeps their session, which is fine at our scale.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: '/auth/change-password',
      data: { current_password: currentPassword, new_password: newPassword },
    });
  }

  /**
   * Persists the caller's UI theme choice. Validated server-side against
   * the enum {light | dark | high-contrast | system}. The frontend updates
   * localStorage + DOM optimistically; failure here just leaves the DB out
   * of sync with the device (preference UX trumps consistency).
   */
  async updateThemePreference(
    theme: 'light' | 'dark' | 'high-contrast'
  ): Promise<APIResponse<{ theme_preference: string }>> {
    return this.request({
      method: 'PATCH',
      url: '/auth/me/theme',
      data: { theme_preference: theme },
    });
  }

  // Product endpoints
  async getProducts(filters?: ProductFilters): Promise<PaginatedResponse<Product[]>> {
    return this.request({
      method: 'GET',
      url: '/products',
      params: filters,
    });
  }

  /**
   * Fetch the ENTIRE product catalogue by walking every page of /products.
   *
   * /products is paginated (default 50, hard-capped at 100 per page server-side),
   * so a single getProducts() call silently returns only the first page. The POS
   * counter's "All" tab needs the whole menu — both to render the grid and so the
   * client-side search box can match products that live past page 1. Calling the
   * paginated endpoint once made the search box appear broken: any product beyond
   * the first 50 (e.g. "crochet flowers" on a 193-item tenant) was never in the
   * array being filtered, so it "didn't exist". Loop until we've drained all pages.
   */
  async getAllProducts(
    filters?: Omit<ProductFilters, 'page' | 'per_page'>
  ): Promise<Product[]> {
    const perPage = 100;
    const all: Product[] = [];
    let page = 1;
    // Backstop the loop so a mis-reported total_pages can never spin forever.
    for (let guard = 0; guard < 1000; guard++) {
      const res = await this.getProducts({ ...filters, page, per_page: perPage });
      const list = Array.isArray(res.data) ? res.data : [];
      all.push(...list);
      const totalPages = res.meta?.total_pages ?? 1;
      if (list.length === 0 || page >= totalPages) break;
      page++;
    }
    return all;
  }

  async getProduct(id: string): Promise<APIResponse<Product>> {
    return this.request({
      method: 'GET',
      url: `/products/${id}`,
    });
  }

  async getCategories(activeOnly = true): Promise<APIResponse<Category[]>> {
    return this.request({
      method: 'GET',
      url: '/categories',
      params: { active_only: activeOnly },
    });
  }

  async getProductsByCategory(
    categoryId: string,
    availableOnly = true
  ): Promise<PaginatedResponse<Product[]>> {
    return this.request({
      method: 'GET',
      url: `/categories/${categoryId}/products`,
      params: { available_only: availableOnly },
    });
  }

  // Order endpoints
  async getOrders(filters?: OrderFilters): Promise<PaginatedResponse<Order[]>> {
    return this.request({
      method: 'GET',
      url: '/orders',
      params: filters,
    });
  }

  async createOrder(order: CreateOrderRequest): Promise<APIResponse<Order>> {
    return this.request({
      method: 'POST',
      url: this.getOrderCreatePath(),
      data: order,
    });
  }

  /**
   * Reserve a block of order numbers for offline use (F-CTRL-N33). The numbers
   * come from the same allocator the counter uses, so one drawn offline can
   * never collide with one issued online.
   */
  async reserveOrderNumbers(count: number): Promise<APIResponse<{ numbers: string[] }>> {
    return this.request({
      method: 'POST',
      url: '/counter/order-numbers/reserve',
      data: { count },
    });
  }

  /** Hand unused reserved numbers back so the day's sequence has no gaps. */
  async releaseOrderNumbers(numbers: string[]): Promise<APIResponse<{ released: number }>> {
    return this.request({
      method: 'POST',
      url: '/counter/order-numbers/release',
      data: { numbers },
    });
  }

  /**
   * Post an order taken while this terminal was offline (F-CTRL-N34).
   *
   * Retry-safe: `op_id` is stored on the order behind a unique index, so a
   * repeat call returns `offline_order_duplicate` rather than creating a
   * second order for food the kitchen already made.
   */
  async replayOfflineOrder(
    op: OfflineReplayRequest,
  ): Promise<APIResponse<{ order_id: string; order_number: string }>> {
    return this.request({
      method: 'POST',
      url: '/counter/offline-orders/replay',
      data: op,
    });
  }

  /**
   * Hand a permanently-failed offline operation to the reconciliation queue
   * (F-CTRL-N35). Idempotent on op_id, so a retried report cannot show the
   * manager the same food twice.
   */
  async reportUnreconciledOffline(item: {
    op_id: string;
    order_number: string;
    target_order_id: string | null;
    captured_at: number;
    failure_code: string;
    lines: unknown;
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({
      method: 'POST',
      url: '/counter/offline-orders/unreconciled',
      data: item,
    });
  }

  /** Open items awaiting a manager's bill-or-write-off decision. */
  async listUnreconciledOffline(): Promise<APIResponse<{ items: UnreconciledOfflineItem[] }>> {
    return this.request({
      method: 'GET',
      url: '/counter/offline-orders/unreconciled',
    });
  }

  /** Record the decision. Manager/admin only; a note is required either way. */
  async resolveUnreconciledOffline(
    id: string,
    action: 'billed' | 'written_off',
    note: string,
  ): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: `/offline-orders/unreconciled/${id}/resolve`,
      data: { action, note },
    });
  }

  async getOrder(id: string): Promise<APIResponse<Order>> {
    return this.request({
      method: 'GET',
      url: `/orders/${id}`,
    });
  }

  async updateOrderStatus(
    id: string,
    status: UpdateOrderStatusRequest['status'],
    notes?: string
  ): Promise<APIResponse<Order>> {
    const statusUpdate: UpdateOrderStatusRequest = { status, notes };
    return this.request({
      method: 'PATCH',
      url: `/orders/${id}/status`,
      data: statusUpdate,
    });
  }

  // Payment endpoints
  async processPayment(orderId: string, payment: ProcessPaymentRequest): Promise<APIResponse<Payment>> {
    return this.request({
      method: 'POST',
      url: this.getProcessPaymentPath(orderId),
      data: payment,
    });
  }

  async getPayments(orderId: string): Promise<APIResponse<Payment[]>> {
    return this.request({
      method: 'GET',
      url: `/orders/${orderId}/payments`,
    });
  }

  async getPaymentSummary(orderId: string): Promise<APIResponse<PaymentSummary>> {
    return this.request({
      method: 'GET',
      url: `/orders/${orderId}/payment-summary`,
    });
  }

  // Dashboard endpoints
  // ─── Legacy snapshot (kept for backward compatibility) ─────────────────
  /**
   * @deprecated Use {@link getDashboardOverview} + {@link getDashboardLive}
   * which return typed payloads with prior-period comparisons.
   */
  async getDashboardStats(): Promise<APIResponse<DashboardStats>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/stats',
    });
  }

  // ─── Dashboard v2 ─────────────────────────────────────────────────────
  // Typed, business-timezone-aware payloads with prior-period comparisons.
  // Powers the redesigned admin dashboard. See backend/internal/handlers/dashboard.go.

  /** Builds the params object for any dashboard v2 endpoint. */
  private dashboardParams(period: DashboardPeriod, from?: string, to?: string) {
    const params: Record<string, string> = { period };
    if (period === 'custom') {
      if (from) params.from = from;
      if (to) params.to = to;
    }
    return params;
  }

  async getDashboardOverview(
    period: DashboardPeriod,
    from?: string,
    to?: string,
  ): Promise<APIResponse<DashboardOverview>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/overview',
      params: this.dashboardParams(period, from, to),
    });
  }

  async getDashboardLive(): Promise<APIResponse<LivePulse>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/live',
    });
  }

  async getDashboardSalesTimeseries(
    period: DashboardPeriod,
    from?: string,
    to?: string,
  ): Promise<APIResponse<SalesTimeseries>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/sales-timeseries',
      params: this.dashboardParams(period, from, to),
    });
  }

  async getDashboardTopItems(
    period: DashboardPeriod,
    opts: { limit?: number; from?: string; to?: string } = {},
  ): Promise<APIResponse<DashboardTopItem[]>> {
    const params = this.dashboardParams(period, opts.from, opts.to);
    if (opts.limit) (params as Record<string, string | number>).limit = opts.limit;
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/top-items',
      params,
    });
  }

  async getDashboardPaymentMix(
    period: DashboardPeriod,
    from?: string,
    to?: string,
  ): Promise<APIResponse<PaymentMixSlice[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/payment-mix',
      params: this.dashboardParams(period, from, to),
    });
  }

  async getDashboardOrderTypeMix(
    period: DashboardPeriod,
    from?: string,
    to?: string,
  ): Promise<APIResponse<OrderTypeMixSlice[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/order-type-mix',
      params: this.dashboardParams(period, from, to),
    });
  }

  async getDashboardAlerts(): Promise<APIResponse<DashboardAlert[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/dashboard/alerts',
    });
  }
  // The SSE stream URL is built inside the useDashboardStream hook (see
  // src/lib/dashboardStream.ts), mirroring the kitchen-stream convention.

  // F-RPT-01: legacy getSalesReport / getOrdersReport / getIncomeReport
  // client methods + their backend endpoints are gone. Use the Reports v2
  // methods below or getDashboardSalesTimeseries instead — both bucket
  // orders by business_day_id so they match Day Close and the dashboard.

  // ─── Reports v2 ──────────────────────────────────────────────────────────
  // All v2 endpoints share a `from`/`to` ISO YYYY-MM-DD contract. The UI
  // displays DD-MM-YYYY but always sends ISO on the wire.

  async getReportsOverview(from: string, to: string): Promise<APIResponse<OverviewReport>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/overview',
      params: { from, to },
    });
  }

  // ─── Marketing Suite landing tab ────────────────────────────────────────
  // Single aggregated endpoint feeding the /admin/marketing/overview page
  // (KPIs + customer mix + engagement timeseries + top performers +
  // needs-attention + occasions + recent activity + channel status).

  async getMarketingOverview(
    from: string,
    to: string,
  ): Promise<APIResponse<MarketingOverviewResponse>> {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/overview',
      params: { from, to },
    });
  }

  async getDailySalesReport(from: string, to: string): Promise<APIResponse<DailySalesRow[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/sales/daily',
      params: { from, to },
    });
  }

  async getHourlySalesReport(from: string, to: string): Promise<APIResponse<HourlySalesReport>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/sales/hourly',
      params: { from, to },
    });
  }

  async getItemSalesReport(
    from: string,
    to: string,
    opts: {
      search?: string;
      category_id?: string;
      tag_id?: string;
      group_by?: 'item' | 'category' | 'tag';
      sort?: 'qty' | 'gross' | 'net' | 'margin' | 'margin_pct';
      limit?: number;
      /** order_type ids to keep (dine_in / takeout / delivery / custom). Empty = all. */
      order_types?: string[];
      /** Orthogonal provenance axis — an online delivery matches both filters. */
      order_source?: 'pos' | 'server' | 'online';
    } = {},
  ): Promise<APIResponse<ItemSalesRow[]>> {
    const params: Record<string, string | number> = { from, to };
    if (opts.search && opts.search.trim() !== '') params.search = opts.search.trim();
    if (opts.category_id) params.category_id = opts.category_id;
    if (opts.tag_id) params.tag_id = opts.tag_id;
    if (opts.group_by) params.group_by = opts.group_by;
    if (opts.order_types && opts.order_types.length > 0) params.order_types = opts.order_types.join(',');
    if (opts.order_source) params.order_source = opts.order_source;
    if (opts.sort) params.sort = opts.sort;
    if (opts.limit) params.limit = opts.limit;
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/items',
      params,
    });
  }

  /** Active tags for the report's Tag group/filter dropdown (accountant-safe). */
  async getReportTags(): Promise<APIResponse<Tag[]>> {
    return this.request({ method: 'GET', url: '/admin/reports/v2/tags' });
  }

  /**
   * Returns a lightweight list of orders for one business day, including the
   * computed PRA late-print eligibility per order. Used by the Reports →
   * Reports Daily Sales tab (drill into a day).
   *
   * `date` accepts ISO YYYY-MM-DD or DD-MM-YYYY. `praFilter` defaults to 'all'.
   */
  async getOrdersBrowser(
    date: string,
    opts: {
      search?: string;
      pra_filter?: 'all' | 'printed' | 'not_printed' | 'eligible';
      fiscal_filter?: 'all' | 'synced' | 'pending' | 'failed' | 'none';
    } = {},
  ): Promise<APIResponse<OrdersBrowserResponse>> {
    const params: Record<string, string> = { date };
    if (opts.search && opts.search.trim() !== '') params.search = opts.search.trim();
    if (opts.pra_filter) params.pra_filter = opts.pra_filter;
    if (opts.fiscal_filter && opts.fiscal_filter !== 'all') params.fiscal_filter = opts.fiscal_filter;
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/orders',
      params,
    });
  }

  /**
   * Cross-day invoice search for the Reports → Daily Sales box. Matches order
   * number, PRA invoice number, FBR IRN, or customer name across all business
   * days. Returns up to 100 matches; the UI deep-links into each match's day.
   */
  async searchInvoices(
    q: string,
  ): Promise<APIResponse<{ results: InvoiceSearchRow[] }>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/orders/search',
      params: { q: q.trim() },
    });
  }

  /** Refund Logs per-day rollup. Reports → Refund Logs tab. */
  async getRefundsDaily(from: string, to: string): Promise<APIResponse<RefundDailyRow[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/refunds/daily',
      params: { from, to },
    });
  }

  async getTaxSummary(from: string, to: string): Promise<APIResponse<TaxSummaryReport>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/tax',
      params: { from, to },
    });
  }

  async getStaffPerformance(from: string, to: string): Promise<APIResponse<StaffPerformanceReport>> {
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/staff',
      params: { from, to },
    });
  }

  /** Credit notes issued on a single day. Reports → Refund Logs drill-in. */
  async getRefundsByDay(
    day: string,
    opts: {
      search?: string;
      status?: 'all' | 'synced' | 'pending' | 'failed';
    } = {},
  ): Promise<APIResponse<RefundLogRow[]>> {
    const params: Record<string, string> = { day };
    if (opts.search && opts.search.trim() !== '') params.search = opts.search.trim();
    if (opts.status && opts.status !== 'all') params.status = opts.status;
    return this.request({
      method: 'GET',
      url: '/admin/reports/v2/refunds/by-day',
      params,
    });
  }

  /**
   * Downloads a report as a CSV file in the user's browser. The filename is
   * the backend's Content-Disposition value — a slug of the restaurant_name
   * setting plus the DD-MM-YYYY range (e.g. "biskits_items_15-06-2026_…csv").
   * That header is only readable cross-origin because the API exposes it via
   * CORS (ExposeHeaders in backend/main.go); the brand-neutral fallback below
   * is a last resort if the header is ever missing.
   */
  async exportReportCsv(
    report: ReportsExportId,
    from: string,
    to: string,
    extra: Record<string, string> = {},
  ): Promise<void> {
    const response = await this.client.get('/admin/reports/v2/export', {
      params: { report, from, to, format: 'csv', ...extra },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Try to honour the server-provided filename; fall back to a sensible name.
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `report_${report}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Per-report formatted .xlsx (the Excel counterpart of exportReportCsv).
   * `report` picks the workbook: overview (Summary/Payment/Order-type sheets),
   * items (Items/Categories/Tags), daily_sales, hourly, tables, party_size,
   * refunds, or period_pack (all reports bundled into one workbook). `extra`
   * passes through Items filters (search/category_id/tag_id).
   */
  async exportReportXlsx(
    report: string,
    from: string,
    to: string,
    extra: Record<string, string> = {},
  ): Promise<void> {
    const response = await this.client.get('/admin/reports/v2/xlsx-export', {
      params: { report, from, to, ...extra },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `report_${report}_${from}_to_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Order-wise + line-wise audit workbook (.xlsx) over a full date range: an
   * Orders sheet (one row per order with the full money + fiscal trail) and a
   * Line Items sheet (one row per item sold). Genuinely auditable — unlike the
   * per-day summary CSV or the on-screen-table PDF.
   */
  async exportOrdersAuditXlsx(from: string, to: string): Promise<void> {
    const response = await this.client.get('/admin/reports/v2/orders/audit-export', {
      params: { from, to },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `orders_audit_${from}_to_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  async updateOrderItemStatus(orderId: string, itemId: string, status: string): Promise<APIResponse> {
    return this.request({
      method: 'PATCH',
      url: `/kitchen/orders/${orderId}/items/${itemId}/status`,
      data: { status },
    });
  }

  /** Recall a bumped order back to the line (within kitchen.recall_window_seconds). */
  async recallOrder(orderId: string): Promise<APIResponse<{ order_id: string; status: string }>> {
    return this.request({
      method: 'POST',
      url: `/kitchen/orders/${orderId}/recall`,
    });
  }

  /** Recently bumped orders (for the KDS recall strip). */
  async getRecentBumpedOrders(limit = 5): Promise<
    APIResponse<
      Array<{
        id: string
        order_number: string
        order_type: string
        customer_name?: string
        table_number?: string | null
        kitchen_bumped_at?: string | null
      }>
    >
  > {
    return this.request({
      method: 'GET',
      url: '/kitchen/recent-bumped',
      params: { limit },
    });
  }

  async createCounterOrder(order: CreateOrderRequest): Promise<APIResponse<Order>> {
    return this.request({
      method: 'POST',
      url: '/counter/orders',
      data: order,
    });
  }

  // Counter payment processing
  async processCounterPayment(orderId: string, payment: ProcessPaymentRequest): Promise<APIResponse<Payment>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/payments`,
      data: payment,
    });
  }

  // ── Online ordering — staff inbox ────────────────────────────────────────
  // Incoming online orders sit in the online_orders staging table until staff
  // accept (which creates the real fiscal order via the shared core) or reject.
  async getOnlineOrders(): Promise<APIResponse<OnlineOrder[]>> {
    return this.request({
      method: 'GET',
      url: '/counter/online-orders',
    });
  }

  // Accepted online orders whose real order is still live, for the dispatch
  // board's In-Kitchen → Ready → Out-for-delivery → Awaiting-settlement lanes.
  async getActiveOnlineOrders(): Promise<APIResponse<ActiveOnlineOrder[]>> {
    return this.request({
      method: 'GET',
      url: '/counter/online-orders/active',
    });
  }

  // Actioned online orders (accepted/rejected), newest-first + paginated, for
  // the Accepted/Rejected history tabs. Mirrors getVoidLog's param shape.
  async getOnlineOrderHistory(params: {
    status: 'accepted' | 'rejected';
    page?: number;
    per_page?: number;
    from?: string;
    to?: string;
    q?: string;
  }): Promise<PaginatedResponse<OnlineOrder[]>> {
    return this.request({
      method: 'GET',
      url: '/counter/online-orders/history',
      params,
    });
  }

  async acceptOnlineOrder(
    id: string,
  ): Promise<APIResponse<{ order: Order; kots: StationKOT[]; fire_failed?: boolean; fire_failed_reason?: string; promo_failed?: boolean; promo_code?: string }>> {
    return this.request({
      method: 'POST',
      url: `/counter/online-orders/${id}/accept`,
    });
  }

  async rejectOnlineOrder(id: string, reason?: string): Promise<APIResponse<null>> {
    return this.request({
      method: 'POST',
      url: `/counter/online-orders/${id}/reject`,
      data: reason ? { reason } : {},
    });
  }

  /**
   * Marks an order as having had its optional PRA (Punjab Revenue Authority)
   * tax invoice slip printed. Safe to call multiple times — the backend
   * refreshes the `pra_invoice_printed_at` timestamp and preserves the
   * existing `pra_invoice_number` when the caller doesn't supply a new one.
   *
   * `invoiceNumber` is optional so the current rollout (which prints the slip
   * with a blank number field) can still log the print event; once a real
   * PRA-issued number is wired up, pass it here.
   */
  async markPraInvoicePrinted(
    orderId: string,
    invoiceNumber?: string,
  ): Promise<APIResponse<null>> {
    const body: { pra_invoice_number?: string } = {};
    if (invoiceNumber && invoiceNumber.trim() !== '') {
      body.pra_invoice_number = invoiceNumber.trim();
    }
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/pra-invoice`,
      data: body,
    });
  }

  /**
   * Customer Directory list — full filter/sort/paginate surface for the
   * /admin/customers page. Supersedes the legacy getAdminCustomers helper.
   */
  async listCustomers(
    params?: import('@/types').CustomerListParams
  ): Promise<APIResponse<import('@/types').CustomerListResponse>> {
    return this.request({
      method: 'GET',
      url: '/admin/customers',
      params,
    });
  }

  async getCustomer(
    id: string
  ): Promise<APIResponse<import('@/types').CustomerDetail>> {
    return this.request({ method: 'GET', url: `/admin/customers/${id}` });
  }

  async getCustomerOrderHistory(
    id: string,
    limit?: number
  ): Promise<APIResponse<import('@/types').CustomerOrderHistoryItem[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/${id}/orders`,
      params: limit ? { limit } : undefined,
    });
  }

  async getCustomerLoyaltyHistory(
    id: string
  ): Promise<APIResponse<import('@/types').CustomerLoyaltyHistory>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/${id}/loyalty-history`,
    });
  }

  /** Manual punch grant (admin/manager + PIN step-up). 1–5 punches, reason
   *  required; lands in the ledger as an attributed source='manual' row. */
  async adjustCustomerLoyalty(
    id: string,
    data: { punches: number; reason: string; pin: string }
  ): Promise<
    APIResponse<{ granted: number; balance: number; threshold: number; adjusted_by: string }>
  > {
    return this.request({
      method: 'POST',
      url: `/admin/customers/${id}/loyalty-adjust`,
      data,
    });
  }

  async updateCustomerNotes(
    id: string,
    notes: string
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'PUT',
      url: `/admin/customers/${id}/notes`,
      data: { notes },
    });
  }

  /**
   * Inline-edit the core profile fields (name, phone, email, birthday).
   * On success returns the refreshed CustomerDetail. On a phone/email
   * collision the backend returns 409 with `error: 'phone_conflict' |
   * 'email_conflict'` and `data` carrying CustomerUpdateConflict — the
   * caller is expected to read both fields.
   */
  async updateCustomer(
    id: string,
    patch: import('@/types').UpdateCustomerRequest
  ): Promise<APIResponse<import('@/types').CustomerDetail | import('@/types').CustomerUpdateConflict>> {
    return this.request({
      method: 'PATCH',
      url: `/admin/customers/${id}`,
      data: patch,
    });
  }

  // ── Customer address book (F-FE-N30) — same customer_addresses table the
  // storefront account edits, so admin and customer always see the same list.

  async listCustomerAddresses(
    customerId: string
  ): Promise<APIResponse<import('@/types').CustomerAddress[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/${customerId}/addresses`,
    });
  }

  async addCustomerAddress(
    customerId: string,
    body: import('@/types').CustomerAddressRequest
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'POST',
      url: `/admin/customers/${customerId}/addresses`,
      data: body,
    });
  }

  async updateCustomerAddress(
    customerId: string,
    addressId: string,
    body: import('@/types').CustomerAddressRequest
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'PUT',
      url: `/admin/customers/${customerId}/addresses/${addressId}`,
      data: body,
    });
  }

  async deleteCustomerAddress(
    customerId: string,
    addressId: string
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'DELETE',
      url: `/admin/customers/${customerId}/addresses/${addressId}`,
    });
  }

  async setCustomerDefaultAddress(
    customerId: string,
    addressId: string
  ): Promise<APIResponse<null>> {
    return this.request({
      method: 'POST',
      url: `/admin/customers/${customerId}/addresses/${addressId}/default`,
    });
  }

  /** Tile grid for the Segments tab — one row per static segment with its count. */
  async listCustomerSegments(): Promise<
    APIResponse<import('@/types').CustomerSegmentSummary[]>
  > {
    return this.request({ method: 'GET', url: '/admin/customers/segments' });
  }

  /** Drill into a specific segment; returns up to `limit` (default 500) customer rows. */
  async getCustomerSegment(
    key: import('@/types').SegmentKey,
    limit?: number
  ): Promise<APIResponse<import('@/types').CustomerSegmentDetail>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/segments/${key}`,
      params: limit ? { limit } : undefined,
    });
  }

  /**
   * Build the auth-aware URL for a CSV export. Triggers a browser download
   * via axios blob fetch — bypassing window.open because the request needs
   * the Authorization header that EventSource/window.open can't carry.
   */
  async downloadCustomerSegmentCSV(
    key: import('@/types').SegmentKey
  ): Promise<Blob> {
    const res = await this.client.get(
      `/admin/customers/segments/${key}`,
      { params: { export: 'csv' }, responseType: 'blob' }
    );
    return res.data;
  }

  /** Likely-duplicate suggestions for a given customer (up to 8). */
  async suggestCustomerDuplicates(
    id: string
  ): Promise<APIResponse<import('@/types').CustomerDuplicateCandidate[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/customers/${id}/duplicates`,
    });
  }

  /** Fold the source customer into the target (audit-logged, soft-delete). */
  async mergeCustomers(
    sourceID: string,
    targetID: string
  ): Promise<APIResponse<{ source_id: string; target_id: string }>> {
    return this.request({
      method: 'POST',
      url: `/admin/customers/${sourceID}/merge`,
      data: { target_id: targetID },
    });
  }

  // ── Promo codes ─────────────────────────────────────────────────────
  async listPromoCodes(params?: {
    include_archived?: boolean;
    include_receipt_codes?: boolean;
  }): Promise<APIResponse<import('@/types').PromoCode[]>> {
    return this.request({ method: 'GET', url: '/admin/marketing/promo-codes', params });
  }

  async createPromoCode(
    data: import('@/types').CreatePromoCodeRequest
  ): Promise<APIResponse<import('@/types').PromoCode>> {
    return this.request({ method: 'POST', url: '/admin/marketing/promo-codes', data });
  }

  async updatePromoCode(
    id: string,
    data: import('@/types').UpdatePromoCodeRequest
  ): Promise<APIResponse<import('@/types').PromoCode>> {
    return this.request({ method: 'PUT', url: `/admin/marketing/promo-codes/${id}`, data });
  }

  async archivePromoCode(id: string): Promise<APIResponse<null>> {
    return this.request({ method: 'POST', url: `/admin/marketing/promo-codes/${id}/archive` });
  }

  /** Counter — current promo redemption state for an open order. */
  async getAppliedPromo(
    orderId: string
  ): Promise<APIResponse<import('@/types').AppliedPromoState>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/applied-promo`,
    });
  }

  /** Counter — apply a typed promo code. Returns 409 with reason on conflict. */
  async applyPromoToOrder(
    orderId: string,
    code: string
  ): Promise<APIResponse<import('@/types').AppliedPromoResult>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/apply-promo`,
      data: { code },
    });
  }

  /** Counter — remove the applied promo. Idempotent. */
  async removePromoFromOrder(orderId: string): Promise<APIResponse<{ applied: boolean }>> {
    return this.request({
      method: 'DELETE',
      url: `/counter/orders/${orderId}/apply-promo`,
    });
  }

  // ── Bank card promos ────────────────────────────────────────────────
  async listCardPromos(
    includeArchived = false
  ): Promise<APIResponse<import('@/types').CardPromo[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/card-promos${includeArchived ? '?include_archived=true' : ''}`,
    });
  }

  async createCardPromo(
    data: import('@/types').CreateCardPromoRequest
  ): Promise<APIResponse<import('@/types').CardPromo>> {
    return this.request({ method: 'POST', url: '/admin/card-promos', data });
  }

  async updateCardPromo(
    id: string,
    data: import('@/types').UpdateCardPromoRequest
  ): Promise<APIResponse<import('@/types').CardPromo>> {
    return this.request({ method: 'PUT', url: `/admin/card-promos/${id}`, data });
  }

  async archiveCardPromo(id: string): Promise<APIResponse<null>> {
    return this.request({ method: 'POST', url: `/admin/card-promos/${id}/archive` });
  }

  /** Counter — card promos valid right now for this order (quick-pick chips). */
  async getEligibleCardPromos(
    orderId: string
  ): Promise<APIResponse<import('@/types').EligibleCardPromo[]>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/eligible-card-promos`,
    });
  }

  /** Counter — current card-promo redemption state for an open order. */
  async getAppliedCardPromo(
    orderId: string
  ): Promise<APIResponse<import('@/types').AppliedCardPromoState>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/applied-card-promo`,
    });
  }

  /** Counter — apply a picked card promo. Returns 409 with reason on conflict. */
  async applyCardPromoToOrder(
    orderId: string,
    cardPromoId: string
  ): Promise<APIResponse<import('@/types').AppliedCardPromoState>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/apply-card-promo`,
      data: { card_promo_id: cardPromoId },
    });
  }

  /** Counter — remove the applied card promo. Idempotent. */
  async removeCardPromoFromOrder(
    orderId: string
  ): Promise<APIResponse<{ applied: boolean }>> {
    return this.request({
      method: 'DELETE',
      url: `/counter/orders/${orderId}/apply-card-promo`,
    });
  }

  /** Reports — per-bank card-promo reimbursement claim report. */
  async getCardPromoReport(
    from: string,
    to: string
  ): Promise<APIResponse<import('@/types').CardPromoClaimReport>> {
    return this.request({
      method: 'GET',
      url: `/admin/reports/v2/card-promos?from=${from}&to=${to}`,
    });
  }

  // ── Loyalty programme ───────────────────────────────────────────────
  async getLoyaltyConfig(): Promise<APIResponse<import('@/types').LoyaltyConfig>> {
    return this.request({ method: 'GET', url: '/admin/marketing/loyalty/config' });
  }

  async updateLoyaltyConfig(
    data: import('@/types').LoyaltyConfig
  ): Promise<APIResponse<import('@/types').LoyaltyConfig>> {
    return this.request({ method: 'PUT', url: '/admin/marketing/loyalty/config', data });
  }

  /** Product ids that earn stamps. An EMPTY array means "all products earn" —
   *  that is the default and is distinct from "no products earn". */
  async getLoyaltyEligibleProducts(): Promise<APIResponse<string[]>> {
    return this.request({ method: 'GET', url: '/admin/marketing/loyalty/eligible-products' });
  }

  /** Replaces the whole eligible-product set. Sending [] clears it, which
   *  re-enables earning on everything. */
  async setLoyaltyEligibleProducts(
    productIds: string[]
  ): Promise<APIResponse<{ product_count: number }>> {
    return this.request({
      method: 'PUT',
      url: '/admin/marketing/loyalty/eligible-products',
      data: { product_ids: productIds },
    });
  }

  async getLoyaltyStats(all = false): Promise<APIResponse<import('@/types').LoyaltyStats>> {
    return this.request({
      method: 'GET',
      url: '/admin/marketing/loyalty/stats',
      params: all ? { balances: 'all' } : undefined,
    });
  }

  async getCustomerLoyaltyStatus(
    customerId: string
  ): Promise<APIResponse<import('@/types').LoyaltyStatus>> {
    return this.request({
      method: 'GET',
      url: `/counter/customers/${customerId}/loyalty-status`,
    });
  }

  async getWalletStats(): Promise<APIResponse<import('@/types').WalletStats>> {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/loyalty/wallet/stats`,
    });
  }

  /** Force-push the restaurant's card branding to Google, restyling every
   *  card already saved on a customer's phone. Saving the loyalty config does
   *  this automatically; this is the manual retry. */
  async syncGoogleWalletClass(): Promise<
    APIResponse<import('@/types').GoogleClassSyncStatus>
  > {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/loyalty/wallet/google/sync`,
    });
  }

  async resolveLoyaltyCard(
    token: string
  ): Promise<APIResponse<import('@/types').ResolvedLoyaltyCard>> {
    return this.request({
      method: 'POST',
      url: `/counter/loyalty/resolve-card`,
      data: { token },
    });
  }

  /** Whether the staff-phone /scan surface is enabled for this venue
   *  (loyalty on AND staff_phone_scan_enabled). Counter-accessible probe —
   *  the full loyalty config stays admin-only. */
  async getPhoneScanEnabled(): Promise<APIResponse<{ enabled: boolean }>> {
    return this.request({
      method: 'GET',
      url: `/counter/loyalty/phone-scan-enabled`,
    });
  }

  async redeemLoyaltyReward(
    orderId: string
  ): Promise<APIResponse<import('@/types').LoyaltyRedeemResult>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/redeem-loyalty`,
    });
  }

  async removeLoyaltyRedemption(orderId: string): Promise<APIResponse<{ applied: boolean }>> {
    return this.request({
      method: 'DELETE',
      url: `/counter/orders/${orderId}/redeem-loyalty`,
    });
  }

  async getOrderLoyaltyClaim(
    orderId: string,
  ): Promise<APIResponse<import('@/types').LoyaltyClaimQR>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/loyalty-claim`,
    });
  }

  async getOrderThankYouPromo(
    orderId: string,
  ): Promise<APIResponse<import('@/types').OrderThankYouCode>> {
    return this.request({
      method: 'GET',
      url: `/counter/orders/${orderId}/thank-you-promo`,
    });
  }

  // ── Recovery coupon — manager-issued comeback code for service recovery ──
  /** Counter — fetch tier presets, override cap and reasons for the
   *  recovery-coupon action (and whether it's enabled at all). */
  async getRecoveryCouponConfig(): Promise<APIResponse<import('@/types').RecoveryCouponConfig>> {
    return this.request({ method: 'GET', url: '/counter/recovery-coupon-config' });
  }

  /** Counter — issue a recovery coupon. Body carries the manager PIN
   *  (verified server-side); 403 on a bad PIN, 400 needs_customer when no
   *  customer is captured on the order. */
  async issueRecoveryCoupon(
    orderId: string,
    data: { pin: string; tier: string; reason: string; override_value?: number },
  ): Promise<APIResponse<import('@/types').IssuedRecoveryCoupon>> {
    return this.request({
      method: 'POST',
      url: `/counter/orders/${orderId}/recovery-coupon`,
      data,
    });
  }

  /**
   * Fetch the printable loyalty poster as raw HTML. Same shape as
   * `downloadCustomerSegmentCSV` — we go through axios so the JWT
   * Authorization header is attached. The caller wraps the response
   * in a blob URL and opens it in a new tab (window.open with the raw
   * /api/v1/... URL can't carry the Bearer token, so it 401s).
   *
   * `layout` selects the format:
   *   • 'a4'   — full-page wall/counter poster (default)
   *   • 'tent' — A5 table tent (foldable, both faces upright)
   *   • 'card' — postcard-sized counter card (148×100 mm)
   */
  async fetchLoyaltyPosterHTML(
    layout: 'a4' | 'tent' | 'card' = 'a4',
  ): Promise<string> {
    const res = await this.client.get('/admin/marketing/loyalty/poster', {
      params: { layout },
      responseType: 'text',
      transformResponse: [(d) => d], // disable JSON.parse
    });
    return String(res.data ?? '');
  }

  // ── Public links (customer-facing base URL) ────────────────────────

  async getPublicLinks(): Promise<APIResponse<import('@/types').PublicLinksConfig>> {
    return this.request({ method: 'GET', url: '/admin/settings/public-links' });
  }

  async updatePublicLinks(
    publicBaseURL: string,
  ): Promise<APIResponse<import('@/types').PublicLinksConfig>> {
    return this.request({
      method: 'PUT',
      url: '/admin/settings/public-links',
      data: { public_base_url: publicBaseURL },
    });
  }

  async searchCustomersQuick(
    q: string,
  ): Promise<APIResponse<{ items: import('@/types').CustomerQuickLookup[] }>> {
    return this.request({
      method: 'GET',
      url: `/counter/customers/search`,
      params: { q },
    });
  }

  // ── Campaigns ──────────────────────────────────────────────────────
  async listCampaigns(): Promise<APIResponse<import('@/types').Campaign[]>> {
    return this.request({ method: 'GET', url: '/admin/marketing/campaigns' });
  }

  async getCampaign(id: string): Promise<APIResponse<import('@/types').Campaign>> {
    return this.request({ method: 'GET', url: `/admin/marketing/campaigns/${id}` });
  }

  async createCampaign(
    data: import('@/types').CreateCampaignRequest
  ): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/admin/marketing/campaigns', data });
  }

  async updateCampaign(
    id: string,
    data: import('@/types').UpdateCampaignRequest
  ): Promise<APIResponse<null>> {
    return this.request({ method: 'PUT', url: `/admin/marketing/campaigns/${id}`, data });
  }

  async getCampaignRecipients(
    id: string,
    channel?: import('@/types').CampaignChannel
  ): Promise<APIResponse<import('@/types').CampaignRecipientsResponse>> {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/campaigns/${id}/recipients`,
      params: channel ? { channel } : undefined,
    });
  }

  async dispatchCampaign(
    id: string,
    data: { channel: 'email'; recipient_ids?: string[] }
  ): Promise<APIResponse<{ channel: string; queued: number; skipped: number }>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/campaigns/${id}/dispatch`,
      data,
    });
  }

  async logCampaignWhatsAppClick(
    id: string,
    customerIds: string[]
  ): Promise<APIResponse<{ logged: number }>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/campaigns/${id}/whatsapp-click`,
      data: { customer_ids: customerIds },
    });
  }

  async previewCampaign(
    id: string
  ): Promise<APIResponse<import('@/types').CampaignPreviewResponse>> {
    return this.request({
      method: 'POST',
      url: `/admin/marketing/campaigns/${id}/preview`,
    });
  }

  async getCounterPricing(): Promise<APIResponse<PricingSettings>> {
    return this.request({ method: 'GET', url: '/counter/pricing' });
  }

  async updateCheckoutIntent(
    orderId: string,
    body: { checkout_payment_method: 'cash' | 'card' | 'online' }
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/checkout-intent`,
      data: body,
    });
  }

  /** Split bill (F-CTRL-N14): persist the split session. Paid markers are
   *  server-owned — this only sends the client-writable shape. */
  async setOrderSplitState(
    orderId: string,
    state: import('@/types').OrderSplitStateInput
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PUT',
      url: `/counter/orders/${orderId}/split`,
      data: state,
    });
  }

  /** Discard the split session (rejected once any guest has paid). */
  async clearOrderSplitState(orderId: string): Promise<APIResponse<Order>> {
    return this.request({
      method: 'DELETE',
      url: `/counter/orders/${orderId}/split`,
    });
  }

  async applyOrderDiscount(
    orderId: string,
    body: { discount_amount?: number; discount_percent?: number }
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/discount`,
      data: body,
    });
  }

  async applyOrderItemDiscount(
    orderId: string,
    itemId: string,
    body: { discount_amount?: number; discount_percent?: number | null; pin?: string }
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/items/${itemId}/discount`,
      data: body,
    });
  }

  /**
   * Set or clear the per-order delivery fee override. Pass a number (>= 0, 0
   * allowed for free delivery) to override; pass null to clear the override and
   * revert to the settings default. Server recomputes totals and returns the
   * updated order.
   */
  async setOrderDeliveryFee(
    orderId: string,
    fee: number | null
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/delivery-fee`,
      data: { delivery_fee: fee },
    });
  }

  async updateCounterOrderGuest(
    orderId: string,
    body: UpdateCounterOrderGuestRequest
  ): Promise<APIResponse<Order>> {
    return this.request({
      method: 'PATCH',
      url: `/counter/orders/${orderId}/guest`,
      data: body,
    });
  }

  // User management endpoints (Admin only)
  async getUsers(params?: { page?: number; limit?: number; per_page?: number; search?: string; role?: string }): Promise<APIResponse<User[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/users',
      params,
    });
  }

  async createUser(userData: any): Promise<APIResponse<User>> {
    return this.request({
      method: 'POST',
      url: '/admin/users',
      data: userData,
    });
  }

  async updateUser(id: string, userData: any): Promise<APIResponse<User>> {
    return this.request({
      method: 'PUT',
      url: `/admin/users/${id}`,
      data: userData,
    });
  }

  async deleteUser(id: string): Promise<APIResponse> {
    return this.request({
      method: 'DELETE',
      url: `/admin/users/${id}`,
    });
  }

  // Admin-specific product management
  async createProduct(productData: any): Promise<APIResponse<Product>> {
    return this.request({ method: 'POST', url: '/admin/products', data: productData });
  }

  async updateProduct(id: string, productData: any): Promise<APIResponse<Product>> {
    return this.request({ method: 'PUT', url: `/admin/products/${id}`, data: productData });
  }

  async deleteProduct(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/products/${id}` });
  }

  // Admin-specific category management  
  async createCategory(categoryData: any): Promise<APIResponse<Category>> {
    return this.request({ method: 'POST', url: '/admin/categories', data: categoryData });
  }

  async updateCategory(id: string, categoryData: any): Promise<APIResponse<Category>> {
    return this.request({ method: 'PUT', url: `/admin/categories/${id}`, data: categoryData });
  }

  async deleteCategory(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/categories/${id}` });
  }

  // Product tags — an overlapping reporting-only label layer (product_tags).
  async getTags(activeOnly = false): Promise<APIResponse<Tag[]>> {
    return this.request({ method: 'GET', url: '/admin/tags', params: activeOnly ? { active_only: true } : {} });
  }

  async createTag(tagData: { name: string; color?: string | null; description?: string | null; sort_order?: number }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/admin/tags', data: tagData });
  }

  async updateTag(id: string, tagData: Partial<{ name: string; color: string | null; description: string | null; sort_order: number; is_active: boolean }>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/tags/${id}`, data: tagData });
  }

  async deleteTag(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/tags/${id}` });
  }

  // Tag-centric item assignment: read/replace the set of products carrying a tag.
  // Drives the Reports tag manager's checkbox picker (the reverse of the
  // per-product tag_ids write path).
  async getTagProducts(id: string): Promise<APIResponse<string[]>> {
    return this.request({ method: 'GET', url: `/admin/tags/${id}/products` });
  }

  async setTagProducts(id: string, productIds: string[]): Promise<APIResponse<{ product_count: number }>> {
    return this.request({ method: 'PUT', url: `/admin/tags/${id}/products`, data: { product_ids: productIds } });
  }

  // Admin products endpoint with pagination
  async getAdminProducts(params?: { page?: number, per_page?: number, limit?: number, search?: string, category_id?: string }): Promise<APIResponse<Product[]>> {
    // Normalize params (handle both per_page and limit)
    const normalizedParams = {
      page: params?.page,
      per_page: params?.per_page || params?.limit,
      search: params?.search,
      category_id: params?.category_id
    }
    
    return this.request({ 
      method: 'GET', 
      url: '/admin/products',
      params: normalizedParams
    });
  }

  // Admin categories endpoint with pagination
  async getAdminCategories(params?: { page?: number, per_page?: number, limit?: number, search?: string, active_only?: boolean }): Promise<APIResponse<Category[]>> {
    // Normalize params (handle both per_page and limit)
    const normalizedParams = {
      page: params?.page,
      per_page: params?.per_page || params?.limit,
      search: params?.search,
      active_only: params?.active_only
    }
    
    return this.request({ 
      method: 'GET', 
      url: '/admin/categories',
      params: normalizedParams
    });
  }

  async listSuppliersViaAdmin(): Promise<APIResponse<Supplier[]>> {
    return this.request({ method: 'GET', url: '/admin/products/suppliers' });
  }

  async getStockItems(params?: {
    page?: number;
    per_page?: number;
    category_id?: string;
    search?: string;
    low_stock?: string;
    stock_health?: 'low' | 'ok';
    sort?: 'category' | 'name' | 'on_hand' | 'reorder' | 'unit_cost' | 'expiry';
    sort_dir?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<StockItem[]>> {
    return this.request({ method: 'GET', url: '/store/stock-items', params });
  }

  async getStockItem(id: string): Promise<APIResponse<StockItem>> {
    return this.request({ method: 'GET', url: `/store/stock-items/${id}` });
  }

  async updateStockItem(id: string, data: Partial<StockItem>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/store/stock-items/${id}`, data });
  }

  async purchaseStock(
    itemId: string,
    data: {
      quantity: number;
      unit_cost?: number;
      note?: string;
      expiry_date?: string;
      supplier_id?: string;
      purchase_order_id?: string;
      purchase_order_line_id?: string;
      post_to_expense?: boolean;
    }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/purchase`, data });
  }

  async issueStock(itemId: string, data: { quantity: number; unit?: string; issued_to_user_id: string; reason?: string; note?: string }): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/issue`, data });
  }

  async adjustStock(
    itemId: string,
    data: { quantity_delta: number; unit_cost?: number; reason?: string; note?: string }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/adjust`, data });
  }

  // Record a physical stock-take count (non-destructive). Drives the true
  // consumption variance (counted − book) in the advanced report.
  async recordStockCount(
    itemId: string,
    data: { counted_quantity: number; note?: string }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/count`, data });
  }

  async returnStockToSupplier(
    itemId: string,
    data: {
      supplier_id: string;
      quantity: number;
      unit_cost?: number;
      refund_amount?: number;
      refund_type: 'cash' | 'bank' | 'supplier_credit' | 'no_refund';
      reason: string;
      pin: string;
    }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-items/${itemId}/supplier-return`, data });
  }

  async getStockAlerts(): Promise<APIResponse<StockAlert[]>> {
    return this.request({ method: 'GET', url: '/store/stock-alerts' });
  }

  async getStockMovements(params?: { page?: number; per_page?: number; category_id?: string; type?: string; from?: string; to?: string }): Promise<PaginatedResponse<StockMovement[]>> {
    return this.request({ method: 'GET', url: '/store/stock-reports/movements', params });
  }

  async getStockSummary(period?: string): Promise<APIResponse<StockSummary>> {
    return this.request({ method: 'GET', url: '/store/stock-reports/summary', params: { period } });
  }

  async getAdvancedStockReport(period?: string): Promise<APIResponse<AdvancedStockReport>> {
    return this.request({ method: 'GET', url: '/store/stock-reports/advanced', params: { period } });
  }

  async getInventoryActivity(params?: {
    page?: number;
    per_page?: number;
    action?: string;
    from?: string;
    to?: string;
  }): Promise<PaginatedResponse<InventoryActivityEntry[]>> {
    return this.request({ method: 'GET', url: '/store/inventory-activity', params });
  }

  async voidPurchaseMovement(movementId: string, data: { reason: string }): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-movements/${movementId}/void`, data });
  }

  async correctPurchaseMovementCost(
    movementId: string,
    data: { unit_cost: number; reason: string }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-movements/${movementId}/correct-purchase-cost`, data });
  }

  async getStoreUsers(): Promise<APIResponse<UserBrief[]>> {
    return this.request({ method: 'GET', url: '/store/users' });
  }

  async listSuppliers(): Promise<APIResponse<Supplier[]>> {
    return this.request({ method: 'GET', url: '/store/suppliers' });
  }

  async createSupplier(data: {
    name: string;
    contact_name?: string;
    phone?: string;
    email?: string;
    notes?: string;
    commission_percent?: number;
    is_consignment?: boolean;
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/store/suppliers', data });
  }

  async updateSupplier(id: string, data: Partial<Supplier>): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/store/suppliers/${id}`, data });
  }

  async deleteSupplier(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/store/suppliers/${id}` });
  }

  async listPurchaseOrders(params?: { page?: number; per_page?: number; status?: string }): Promise<PaginatedResponse<PurchaseOrderSummary[]>> {
    return this.request({ method: 'GET', url: '/store/purchase-orders', params });
  }

  async getPurchaseOrder(id: string): Promise<APIResponse<PurchaseOrderDetail>> {
    return this.request({ method: 'GET', url: `/store/purchase-orders/${id}` });
  }

  async createPurchaseOrder(data: {
    supplier_id: string;
    expected_date?: string;
    notes?: string;
    lines: { stock_item_id: string; quantity_ordered: number; unit_cost?: number }[];
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/store/purchase-orders', data });
  }

  async approvePurchaseOrder(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/approve` });
  }

  async sendPurchaseOrderWhatsApp(id: string): Promise<APIResponse<{ wa_url: string }>> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/whatsapp` });
  }

  // Vendor accounts-payable
  async listVendorBills(params?: { supplier_id?: string; status?: string }): Promise<APIResponse<VendorBill[]>> {
    return this.request({ method: 'GET', url: '/store/vendor-bills', params });
  }

  async updateVendorBill(
    id: string,
    data: { invoice_number?: string; bill_date?: string; due_date?: string; amount?: number; notes?: string; void?: boolean },
  ): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/store/vendor-bills/${id}`, data });
  }

  async createVendorPayment(data: {
    supplier_id?: string;
    vendor_bill_id?: string;
    amount: number;
    method?: 'cash' | 'bank';
    note?: string;
    /** B-P1: stable key per submit intent so a retry can't double-pay. */
    idempotency_key?: string;
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/store/vendor-payments', data });
  }

  // #6: sale-time stock deduction failures (dead-letter) — list + resolve.
  async getStockDeductionFailures(): Promise<
    APIResponse<{ id: string; order_number?: string; product_name?: string; reason?: string; created_at: string }[]>
  > {
    return this.request({ method: 'GET', url: '/store/stock-deduction-failures' });
  }

  async resolveStockDeductionFailure(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/stock-deduction-failures/${id}/resolve` });
  }

  async getVendorAging(): Promise<APIResponse<VendorAgingRow[]>> {
    return this.request({ method: 'GET', url: '/store/vendor-aging' });
  }

  async getVendorLedger(supplierId: string): Promise<APIResponse<VendorLedgerEntry[]>> {
    return this.request({ method: 'GET', url: '/store/vendor-ledger', params: { supplier_id: supplierId } });
  }

  // Multi-sheet .xlsx inventory reports export (Stock, Purchase Orders, Sales,
  // Consumption Variance, Vendor Aging, Vendor Ledger). Streams a blob.
  /**
   * Inventory .xlsx export. Omit `sheet` (or pass 'all') for the full multi-sheet
   * workbook; pass a single report slug (stock | purchase_orders | sales |
   * consumption_variance | vendor_aging | vendor_ledger) to download that one
   * report on its own.
   */
  async exportInventoryReportsXlsx(from: string, to: string, sheet?: string): Promise<void> {
    const params: Record<string, string> = { from, to };
    if (sheet && sheet !== 'all') params.sheet = sheet;
    const response = await this.client.get('/store/inventory-reports/export', {
      params,
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `inventory_${sheet ?? 'reports'}_${from}_to_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // ── Vendor-wise (consignment) reports ────────────────────────────────────
  async getVendorSalesReport(supplierId: string, from: string, to: string): Promise<APIResponse<VendorSalesReport>> {
    return this.request({ method: 'GET', url: '/store/vendor-reports/sales', params: { supplier_id: supplierId, from, to } });
  }

  async getVendorStockReport(supplierId: string): Promise<APIResponse<VendorStockReport>> {
    return this.request({ method: 'GET', url: '/store/vendor-reports/stock', params: { supplier_id: supplierId } });
  }

  /** Generate (idempotently) a consignment settlement bill = the vendor's payout for the period. */
  async createVendorSettlement(supplierId: string, from: string, to: string): Promise<APIResponse<{ settlement: VendorSettlement; already_existed?: boolean }>> {
    return this.request({ method: 'POST', url: '/store/vendor-reports/settlement', params: { supplier_id: supplierId, from, to } });
  }

  /** Streams a single-report .xlsx (sales or stock) so the two download separately. */
  async exportVendorReportXlsx(report: 'sales' | 'stock', supplierId: string, from?: string, to?: string): Promise<void> {
    const params: Record<string, string> = { report, supplier_id: supplierId };
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await this.client.get('/store/vendor-reports/export', { params, responseType: 'blob' });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `vendor_${report}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // Per-vendor .xlsx statement for a date range: running-balance Ledger, Items
  // Purchased, and Payments. Streams a blob.
  async exportVendorLedgerXlsx(supplierId: string, from: string, to: string): Promise<void> {
    const response = await this.client.get('/store/vendor-ledger/export', {
      params: { supplier_id: supplierId, from, to },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    const match = cd && /filename="?([^"]+)"?/i.exec(cd);
    a.download = match ? match[1] : `vendor_ledger_${from}_to_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  async submitPurchaseOrder(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/submit` });
  }

  async cancelPurchaseOrder(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/cancel` });
  }

  async deletePurchaseOrder(id: string, data: { pin: string; reason: string }): Promise<APIResponse<{ authorized_by?: string }>> {
    return this.request({ method: 'DELETE', url: `/store/purchase-orders/${id}`, data });
  }

  async receivePurchaseOrder(
    id: string,
    data: { lines: { line_id: string; quantity_received: number; unit_cost?: number; expiry_date?: string; variance_reason?: string }[]; finalize?: boolean; idempotency_key?: string; post_to_expense?: boolean }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/receive`, data });
  }

  async closePurchaseOrder(id: string, reason: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/close`, data: { reason } });
  }

  async returnPurchaseOrderLine(
    id: string,
    data: {
      purchase_order_line_id: string;
      quantity: number;
      unit_cost?: number;
      refund_amount?: number;
      refund_type: 'cash' | 'bank' | 'supplier_credit' | 'no_refund';
      reason: string;
      pin: string;
    }
  ): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/store/purchase-orders/${id}/returns`, data });
  }

  // Expense endpoints
  async getExpenses(params?: {
    page?: number;
    per_page?: number;
    category?: string;
    from?: string;
    to?: string;
    search?: string;
    sort_by?: 'expense_date' | 'amount' | 'category' | 'description' | 'created_by' | 'type' | 'created_at';
    sort_dir?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Expense[]>> {
    return this.request({ method: 'GET', url: '/admin/expenses', params });
  }

  async createExpense(data: {
    category: string;
    amount: number;
    description?: string;
    expense_date?: string;
    recorded_at?: string;
  }): Promise<APIResponse<{ id: string }>> {
    return this.request({ method: 'POST', url: '/admin/expenses', data });
  }

  async updateExpense(id: string, data: {
    category?: string;
    amount?: number;
    description?: string;
    expense_date?: string;
    recorded_at?: string;
  }): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/expenses/${id}`, data });
  }

  async deleteExpense(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/expenses/${id}` });
  }

  async getExpenseSummary(params?: { from?: string; to?: string }): Promise<APIResponse<ExpenseSummary>> {
    return this.request({ method: 'GET', url: '/admin/expenses/summary', params });
  }

  async getExpenseCategories(): Promise<APIResponse<{ category: string; count: number; total: number }[]>> {
    return this.request({ method: 'GET', url: '/admin/expenses/categories' });
  }

  async getExpenseCategoryDefinitions(): Promise<APIResponse<ExpenseCategoryDefinition[]>> {
    return this.request({ method: 'GET', url: '/admin/expense-category-definitions' });
  }

  async createExpenseCategoryDefinition(data: { label: string; color?: string; sort_order?: number }): Promise<APIResponse<{ id: string; slug: string }>> {
    return this.request({ method: 'POST', url: '/admin/expense-category-definitions', data });
  }

  async updateExpenseCategoryDefinition(
    id: string,
    data: { label?: string; color?: string; sort_order?: number; is_active?: boolean }
  ): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/expense-category-definitions/${id}`, data });
  }

  async deleteExpenseCategoryDefinition(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/expense-category-definitions/${id}` });
  }

  // Daily Closing endpoints (read-only — the legacy daily_closings ledger
  // is now mirrored from the new dayops lifecycle on lock-close).
  async getDailyClosings(params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<DailyClosing[]>> {
    return this.request({ method: 'GET', url: '/admin/daily-closings', params });
  }

  async getCurrentDayStatus(): Promise<APIResponse<CurrentDayStatus>> {
    return this.request({ method: 'GET', url: '/admin/daily-closings/current' });
  }

  async getDailyClosingByDate(date: string): Promise<APIResponse<DailyClosing>> {
    return this.request({ method: 'GET', url: `/admin/daily-closings/${date}` });
  }

  // ── Day Operations (business_days lifecycle) ────────────────────────
  // The Day Close page uses these. Counter (`/day-ops/current`) reads
  // the same status pill via a counter-scoped route mounted in routes.go.

  /** Returns the currently-open or staged business day, or null when none. */
  async getDayOpsCurrent(): Promise<APIResponse<BusinessDay | null>> {
    return this.request({ method: 'GET', url: '/admin/day-ops/current' });
  }

  /** Counter-scope read of the same data — accessible to counter staff. */
  async getDayOpsCurrentCounter(): Promise<APIResponse<BusinessDay | null>> {
    return this.request({ method: 'GET', url: '/day-ops/current' });
  }

  /** Real-value open orders sitting open longer than `hours` (default 24) — the aging alert. */
  async getAgingOpenOrders(hours = 24): Promise<APIResponse<AgingOpenOrdersResult>> {
    return this.request({ method: 'GET', url: `/admin/day-ops/aging-open-orders?hours=${hours}` });
  }

  /** Card/online payments swiped on a day but booked earlier — day-close tender reconciliation. */
  async getTenderReconciliation(dayId: string): Promise<APIResponse<TenderReconResult>> {
    return this.request({ method: 'GET', url: `/admin/day-ops/days/${dayId}/tender-reconciliation` });
  }

  async openBusinessDay(data: {
    opening_cash: number;
    notes?: string;
  }): Promise<APIResponse<BusinessDay>> {
    return this.request({ method: 'POST', url: '/admin/day-ops/open', data });
  }

  async getBusinessDay(id: string): Promise<APIResponse<BusinessDay>> {
    return this.request({ method: 'GET', url: `/admin/day-ops/days/${id}` });
  }

  async listDayCashMovements(id: string): Promise<APIResponse<CashDrawerMovement[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/day-ops/days/${id}/cash-movements`,
    });
  }

  async addDayCashMovement(data: {
    movement_type: 'paid_in' | 'paid_out';
    amount: number;
    reason: string;
    notes?: string;
    expense_category?: string;
  }): Promise<APIResponse<CashDrawerMovement>> {
    return this.request({ method: 'POST', url: '/admin/day-ops/cash-movements', data });
  }

  async updateCashMovement(
    id: string,
    data: { amount: number; reason: string; notes?: string },
  ): Promise<APIResponse<CashDrawerMovement>> {
    return this.request({ method: 'PUT', url: `/admin/day-ops/cash-movements/${id}`, data });
  }

  async deleteCashMovement(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/day-ops/cash-movements/${id}` });
  }

  async listDayAudit(id: string): Promise<APIResponse<DayCloseAuditEntry[]>> {
    return this.request({ method: 'GET', url: `/admin/day-ops/days/${id}/audit` });
  }

  async stageCloseDay(
    id: string,
    data: {
      counted_cash: number;
      // Required (F-CTRL-N33). Every tender is counted by a human — enter 0 if
      // it took nothing. The backend rejects an omitted field with 422
      // `tender_count_required`; making them non-optional here means a caller
      // that forgets fails at compile time instead of at the till.
      counted_card: number;
      counted_online: number;
      notes?: string;
    },
  ): Promise<APIResponse<BusinessDay>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/stage-close`,
      data,
    });
  }

  async lockCloseDay(id: string): Promise<APIResponse<BusinessDay>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/lock-close`,
    });
  }

  /**
   * Close a shift mid-day (F-CTRL-N29) — a drawer reconciliation at handover.
   * The day stays open. counted_card / counted_online are optional (skippable);
   * cash_drop is the amount moved to the safe (0 = none, next shift opens on the
   * full counted amount). Attributed to the logged-in user, no PIN.
   */
  async shiftCloseDay(
    id: string,
    data: {
      counted_cash: number;
      // Required — same rule as the day close (F-CTRL-N33).
      counted_card: number;
      counted_online: number;
      cash_drop?: number;
      notes?: string;
    },
  ): Promise<APIResponse<ShiftClosing>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/shift-close`,
      data,
    });
  }

  async getShiftClosings(id: string): Promise<APIResponse<ShiftClosing[]>> {
    return this.request({
      method: 'GET',
      url: `/admin/day-ops/days/${id}/shift-closings`,
    });
  }

  /** Undo the most-recent shift close (reverses its cash drop) while the day is open. */
  async undoLastShiftClose(id: string): Promise<APIResponse<null>> {
    return this.request({
      method: 'DELETE',
      url: `/admin/day-ops/days/${id}/shift-closings/last`,
    });
  }

  /**
   * Build the printable Z report payload for a staged or closed day.
   * On first call for a closed day, a monotonic Z sequence is assigned;
   * subsequent calls return the same sequence (idempotent reprints).
   */
  async getZReport(id: string): Promise<APIResponse<ZReportPayload>> {
    return this.request({
      method: 'GET',
      url: `/admin/day-ops/days/${id}/z-report`,
    });
  }

  async reopenBusinessDay(
    id: string,
    data: { pin: string },
  ): Promise<APIResponse<BusinessDay>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/reopen`,
      data,
    });
  }

  /**
   * Force-close a stale prior-date day WITHOUT recording a cash count.
   * Use this only when a count is genuinely impossible (shop was closed,
   * drawer emptied, leftover setup-time data). Backend constraints:
   * business_date must be strictly < today, reason must be 4–500 chars
   * after trim, and the PIN must belong to an active admin or manager.
   * On success the day's business_days row flips to status='closed' with
   * counted_cash / cash_variance left NULL — reports use that NULL
   * signature to distinguish "no count" from "counted zero."
   */
  async forceCloseBusinessDay(
    id: string,
    data: { pin: string; reason: string },
  ): Promise<APIResponse<BusinessDay>> {
    return this.request({
      method: 'POST',
      url: `/admin/day-ops/days/${id}/force-close`,
      data,
    });
  }

  /**
   * Daily Closing handoff. The current session (typically a cashier) sends
   * a manager/admin PIN; on success the response carries a fresh JWT for
   * that manager user. Call setAuth(token, user) with the response to swap
   * the device into the manager's session, then navigate to /admin/day-close.
   */
  async elevateSession(pin: string): Promise<APIResponse<LoginResponse>> {
    return this.request({
      method: 'POST',
      url: '/admin/day-ops/elevate-session',
      data: { pin },
    });
  }

  async listDayOpsHistory(params?: {
    limit?: number;
    offset?: number;
    /** YYYY-MM-DD — when both from & to are set, the backend returns all
     *  rows in that inclusive range and ignores limit/offset. */
    from?: string;
    to?: string;
  }): Promise<PaginatedResponse<BusinessDay[]>> {
    return this.request({ method: 'GET', url: '/admin/day-ops/history', params });
  }

  async getDayOpsHistoryBounds(): Promise<
    APIResponse<{ earliest?: string | null; latest?: string | null }>
  > {
    return this.request({ method: 'GET', url: '/admin/day-ops/history/bounds' });
  }

  // P&L Report endpoint
  async getPnLReport(params?: { period?: string; from?: string; to?: string }): Promise<APIResponse<PnLReport>> {
    return this.request({ method: 'GET', url: '/admin/reports/pnl', params });
  }

  async getExpenseIntelligence(params?: { period_days?: number }): Promise<APIResponse<ExpenseIntelligenceReport>> {
    return this.request({ method: 'GET', url: '/admin/reports/expense-intelligence', params });
  }

  /**
   * Append items to an existing order.
   *
   * `offlineOpId` makes the call retry-safe for offline replay (F-CTRL-N37):
   * the backend claims it inside the same transaction as the insert, so a
   * retried replay is rejected with `offline_order_duplicate` rather than
   * appending the same items — and the food being made — twice. Omitted on
   * every live path.
   */
  async addItemsToOrder(
    orderId: string,
    items: { product_id: string; quantity: number; special_instructions?: string; course_number?: number }[],
    offlineOpId?: string,
  ): Promise<APIResponse> {
    return this.request({
      method: 'POST',
      url: `/orders/${orderId}/items`,
      data: offlineOpId ? { items, offline_op_id: offlineOpId } : { items },
    });
  }

  /** Order-level kitchen note (printed on KOT). Floor + counter. */
  async updateOrderNotes(orderId: string, notes: string): Promise<APIResponse> {
    return this.request({ method: 'PATCH', url: `/orders/${orderId}/notes`, data: { notes } });
  }

  async removeDraftItem(orderId: string, itemId: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/orders/${orderId}/items/${itemId}` });
  }

  async voidItem(orderId: string, itemId: string, data: { pin: string; reason: string; quantity?: number }): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/orders/${orderId}/items/${itemId}/void`, data });
  }

  // PIN verification
  async verifyPin(pin: string): Promise<APIResponse<{ valid: boolean; user_name?: string }>> {
    return this.request({ method: 'POST', url: '/verify-pin', data: { pin } });
  }

  // PIN management (admin)
  async setUserPin(userId: string, pin: string): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/users/${userId}/pin`, data: { pin } });
  }

  // Void log (admin)
  async getVoidLog(params?: {
    page?: number
    per_page?: number
    from?: string
    to?: string
    /** Legacy combined filter — matches voider OR authorizer. Prefer voided_by/authorized_by. */
    user_id?: string
    voided_by?: string
    authorized_by?: string
    reason?: string
    order_number?: string
    min_value?: number
  }): Promise<PaginatedResponse<VoidLogEntry[]>> {
    return this.request({ method: 'GET', url: '/admin/void-log', params });
  }

  // App Settings
  async getSetting(key: string): Promise<APIResponse<any>> {
    return this.request({ method: 'GET', url: `/settings/${key}` });
  }

  async updateSetting(key: string, value: any): Promise<APIResponse<any>> {
    return this.request({ method: 'PUT', url: `/admin/settings/${key}`, data: value });
  }

  async getAllSettings(): Promise<APIResponse<Record<string, any>>> {
    return this.request({ method: 'GET', url: '/settings' });
  }

  // WhatsApp Commerce catalog
  async getCatalogStatus(): Promise<
    APIResponse<{
      configured: boolean;
      catalog_id: string;
      auto_sync_enabled: boolean;
      available_products: number;
      catalog_ready: number;
      missing_image: number;
      feed_url: string;
    }>
  > {
    return this.request({ method: 'GET', url: '/admin/whatsapp/catalog/status' });
  }

  async syncCatalogNow(): Promise<APIResponse<{ pushed: number; skipped: number }>> {
    return this.request({ method: 'POST', url: '/admin/whatsapp/catalog/sync' });
  }

  async backfillProductImages(): Promise<
    APIResponse<{ migrated: number; failed: number; remaining: number; total: number }>
  > {
    return this.request({ method: 'POST', url: '/admin/products/migrate-images' });
  }

  async exportCatalogCsv(): Promise<void> {
    const response = await this.client.get('/admin/whatsapp/catalog/export.csv', {
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'whatsapp_catalog.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // Configurable online payment sub-methods (Easypaisa/JazzCash/…), migration 050.
  async getOnlineSubMethods(): Promise<APIResponse<OnlineSubMethodConfig>> {
    return this.request({ method: 'GET', url: '/admin/payment-submethods' });
  }

  async updateOnlineSubMethods(
    config: OnlineSubMethodConfig,
  ): Promise<APIResponse<OnlineSubMethodConfig>> {
    return this.request({ method: 'PUT', url: '/admin/payment-submethods', data: config });
  }

  // Dynamic marketing QR codes — each row owns an immutable slug that is
  // encoded in the printed PNG. Editing target_url retroactively retargets
  // every QR already in circulation; scan analytics come from /q/:slug hits.
  async listMarketingQR(): Promise<APIResponse<MarketingQRListResponse>> {
    return this.request({ method: 'GET', url: '/admin/marketing/qr' });
  }

  async bootstrapMarketingQR(): Promise<APIResponse> {
    return this.request({ method: 'POST', url: '/admin/marketing/qr/bootstrap' });
  }

  // Optional label seeds the new row; empty body lets the backend pick
  // a "QR N" default. Returns the new slug + id; the caller refetches
  // the list to get the full row shape with style + stats.
  async createMarketingQR(
    label?: string,
  ): Promise<APIResponse<import('@/types').CreateMarketingQRResponse>> {
    return this.request({
      method: 'POST',
      url: '/admin/marketing/qr',
      data: label && label.trim() !== '' ? { label: label.trim() } : {},
    });
  }

  async deleteMarketingQR(id: string): Promise<APIResponse> {
    return this.request({ method: 'DELETE', url: `/admin/marketing/qr/${id}` });
  }

  async updateMarketingQR(id: string, data: UpdateMarketingQRRequest): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: `/admin/marketing/qr/${id}`, data });
  }

  async getMarketingQRTimeseries(
    id: string,
    days = 30,
  ): Promise<APIResponse<MarketingQRTimeseriesResponse>> {
    return this.request({
      method: 'GET',
      url: `/admin/marketing/qr/${id}/timeseries`,
      params: { days },
    });
  }

  // Pass redirect_base = '' to clear the override and let the backend
  // fall through to MARKETING_QR_REDIRECT_BASE env / request auto-detect.
  async updateMarketingQRConfig(redirectBase: string): Promise<APIResponse> {
    return this.request({
      method: 'PUT',
      url: '/admin/marketing/qr/config',
      data: { redirect_base: redirectBase },
    });
  }

  // Fiscal (FBR/PRA) — admin only
  async getFiscalConfig(): Promise<APIResponse<FiscalConfigPublic>> {
    return this.request({ method: 'GET', url: '/admin/fiscal/config' });
  }

  async putFiscalConfig(data: {
    authority: string
    pos_id: string
    ntn: string
    is_sandbox: boolean
    strn?: string
    pntn?: string
    pos_registration_number?: string
    sfd_proxy_url?: string
    pos_post_url?: string
    fbr_validate_url?: string
    fbr_post_url?: string
    seller_business_name?: string
    seller_province?: string
    seller_address?: string
    buyer_registration_default?: string
    api_key?: string
  }): Promise<APIResponse> {
    return this.request({ method: 'PUT', url: '/admin/fiscal/config', data });
  }

  async postFiscalTestConnection(body: { authority?: string }): Promise<APIResponse<FiscalTestConnectionResult>> {
    return this.request({ method: 'POST', url: '/admin/fiscal/test-connection', data: body });
  }

  async postFiscalDiSn002SandboxValidate(): Promise<APIResponse<FiscalDiSn002SandboxValidateResult>> {
    return this.request({
      method: 'POST',
      url: '/admin/fiscal/sn002/validate',
    });
  }

  // Fiscal credit notes. The schema retains the credit/debit union for
  // forward compatibility, but the application only exposes credit notes
  // (the FBR-approved payload uses InvoiceType=3 for credit cancellation).
  async postFiscalNote(data: CreateFiscalNoteRequest): Promise<APIResponse<FiscalNote>> {
    return this.request({ method: 'POST', url: '/admin/fiscal/notes', data });
  }

  async getFiscalNotes(params?: { order_id?: string; note_type?: string }): Promise<APIResponse<FiscalNote[]>> {
    return this.request({ method: 'GET', url: '/admin/fiscal/notes', params });
  }

  async getFiscalNote(id: string): Promise<APIResponse<FiscalNote>> {
    return this.request({ method: 'GET', url: `/admin/fiscal/notes/${id}` });
  }

  /** Order-scoped, PIN-gated full credit note. Replaces the destructive
   * "cancel a synced invoice" path: posts a credit note to FBR, marks the
   * original order cancelled_by_credit_note, and writes a fiscal_audit_event.
   *
   * Tries `/admin/orders/:id/credit-note` first (legacy path shown in older
   * bundles), then `/admin/fiscal/orders/:id/credit-note` on 404 so fiscal-
   * scoped deploys still work when there is deploy skew or CDN caching. */
  async issueOrderCreditNote(
    orderId: string,
    data: IssueCreditNoteRequest,
  ): Promise<APIResponse<FiscalNote>> {
    const urls = [
      `/admin/orders/${orderId}/credit-note`,
      `/admin/fiscal/orders/${orderId}/credit-note`,
    ] as const;
    for (let i = 0; i < urls.length; i++) {
      try {
        return await this.request<APIResponse<FiscalNote>>({
          method: 'POST',
          url: urls[i],
          data,
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : '';
        const looksLike404 =
          msg.includes('404') ||
          /\bnot\s*found\b/i.test(msg);
        if (looksLike404 && i < urls.length - 1) {
          continue;
        }
        throw e;
      }
    }
    throw new Error('Credit note request failed');
  }

  async getFiscalAuditEvents(params?: {
    order_id?: string;
    phase?: string;
    outcome?: string;
    workflow?: string;
  }): Promise<APIResponse<FiscalAuditEvent[]>> {
    return this.request({ method: 'GET', url: '/admin/fiscal/audit-events', params });
  }

  /** Live fiscal_outbound_jobs queue (submission state). status: '' / 'all' / 'dead' / 'pending' / … */
  async getFiscalJobs(status?: string): Promise<APIResponse<FiscalJob[]>> {
    return this.request({
      method: 'GET',
      url: '/admin/fiscal/jobs',
      params: status && status !== 'all' ? { status } : undefined,
    });
  }

  /** Re-queue a single dead/cancelled fiscal job (same USIN; no payload change). */
  async retryFiscalJob(id: string): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/admin/fiscal/jobs/${id}/retry` });
  }

  /** Re-queue every dead fiscal job that has no competing active job. */
  async retryAllFiscalJobs(): Promise<APIResponse<{ retried_count: number }>> {
    return this.request({ method: 'POST', url: '/admin/fiscal/jobs/retry-all' });
  }

  /**
   * PRA claims stuck in-flight — the reconciliation worklist.
   *
   * PRA is not idempotent and offers no query-by-USIN endpoint, so when a POST
   * fails ambiguously the system cannot tell whether PRA filed the invoice. It
   * parks the claim and stops rather than risk a duplicate filing on a tax
   * record. These are the orders waiting on a human to check the PRA portal.
   */
  async getPRAUnresolved(): Promise<APIResponse<PRAUnresolvedClaim[]>> {
    return this.request({ method: 'GET', url: '/admin/fiscal/pra/unresolved' });
  }

  /**
   * Close one in-flight PRA claim with an operator's verdict.
   *
   * 'found'  — the invoice IS in the PRA portal. Records its number and marks
   *            the order filed. Never resubmits.
   * 'absent' — PRA never filed it, so resubmission is safe. Releases the claim
   *            and re-queues the order.
   *
   * This is the only path that turns an unknown into a resubmit, and it
   * requires someone who has actually looked.
   */
  async resolvePRAClaim(
    id: string,
    data: { action: 'found' | 'absent'; invoice_number?: string; note?: string },
  ): Promise<APIResponse<{ requeued?: boolean }>> {
    return this.request({ method: 'POST', url: `/admin/fiscal/pra/${id}/resolve`, data });
  }

  /** LOCAL_BRIDGE: send the IMSFiscal localhost response back to the backend for persistence. */
  async postLocalFiscalResult(orderId: string, data: {
    invoice_number: string
    qr_code: string
    raw_response: string
    error: string
  }): Promise<APIResponse> {
    return this.request({ method: 'POST', url: `/counter/orders/${orderId}/local-fiscal-result`, data });
  }

  // Utility methods
  setAuthToken(token: string): void {
    localStorage.setItem('pos_token', token);
  }

  /** Writes both the JWT and the user object — mirrors what /login does. */
  setAuth(token: string, user: User): void {
    localStorage.setItem('pos_token', token);
    localStorage.setItem('pos_user', JSON.stringify(user));
  }

  clearAuth(): void {
    localStorage.removeItem('pos_token');
    localStorage.removeItem('pos_user');
  }

  getAuthToken(): string | null {
    return localStorage.getItem('pos_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

// Create and export a singleton instance
export const apiClient = new APIClient();
export default apiClient;

